/**
 * NOTE: This code belongs to Kangaroo Rewards. Unauthorized use is prohibited
 */

@file:Suppress("unused", "MemberVisibilityCanBePrivate", "EXPERIMENTAL_API_USAGE")
@file:OptIn(ExperimentalJsExport::class)
package features.getPublicQrCodeScanToken

import kangaroorewards.appsdk.core.api.Api
import kangaroorewards.appsdk.core.domain.Result
import kangaroorewards.appsdk.core.domain.Result.Idle
import kangaroorewards.appsdk.core.domain.Result.Loading
import kangaroorewards.appsdk.core.domain.SerializedResult
import kangaroorewards.appsdk.core.domain.toJsonResult
import kangaroorewards.appsdk.core.utils.CFlow
import kangaroorewards.appsdk.core.utils.asCFlow
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.mapLatest
import kotlinx.serialization.InternalSerializationApi
import kotlin.js.ExperimentalJsExport
import features.getPublicQrCodeScanToken.internal.GetPublicQrCodeScanTokenUseCase
import features.getPublicQrCodeScanToken.internal.GetPublicQrCodeScanTokenUseCase.Params
import features.getPublicQrCodeScanToken.models.GetPublicQrCodeScanTokenResponseModel
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import features.getPublicQrCodeScanToken.models.PublicQrCodeScanTokenRequest

typealias GetPublicQrCodeScanTokenState = CFlow<GetPublicQrCodeScanTokenUseCaseResult?>

typealias GetPublicQrCodeScanTokenUseCaseResult = Result<GetPublicQrCodeScanTokenResponseModel>

fun GetPublicQrCodeScanTokenUseCaseResult.serializeGetPublicQrCodeScanTokenApiResult(): SerializedResult<String> {
    return this.toJsonResult<GetPublicQrCodeScanTokenResponseModel>()
}

/**
 * Serializes [GetPublicQrCodeScanTokenState]'s data and returns a new
 * [CFlow] containing data serialized as a JSON [String].
 */
@OptIn(ExperimentalCoroutinesApi::class)
fun GetPublicQrCodeScanTokenState.serializeGetPublicQrCodeScanTokenState(): CFlow<SerializedResult<String>?> {
    return this.mapLatest {
        it?.toJsonResult<GetPublicQrCodeScanTokenResponseModel>()
    }.asCFlow()
}

/**
 * Get Public QR Code Scan Token [Api] class
 */
class GetPublicQrCodeScanTokenApi : Api() {

    val getPublicQrCodeScanTokenState: GetPublicQrCodeScanTokenState
        get() = _getPublicQrCodeScanTokenState.asCFlow()

    companion object {
        private val _getPublicQrCodeScanTokenState: MutableStateFlow<GetPublicQrCodeScanTokenUseCaseResult> = MutableStateFlow(Idle())
    }

    private val getPublicQrCodeScanTokenUseCase = GetPublicQrCodeScanTokenUseCase()

    /**
     * Get Public QR Code Scan Token
     */
    suspend fun getPublicQrCodeScanToken(
        overrideHeaders: Map<String, String>? = null,
        publicQrCodeScanTokenRequest: PublicQrCodeScanTokenRequest
    ): Result<GetPublicQrCodeScanTokenResponseModel> {
        /* set state to Loading as soon as function is called */
        _getPublicQrCodeScanTokenState.value = Loading()
        val result = this.getPublicQrCodeScanTokenUseCase(
            Params(
                overrideHeaders = overrideHeaders,
                publicQrCodeScanTokenRequest = publicQrCodeScanTokenRequest
            )
        ) { result ->
            _getPublicQrCodeScanTokenState.value = result
            return@getPublicQrCodeScanTokenUseCase result
        }
        return result
    }

    /**
    * Get Public QR Code Scan Token
    *
    * This method is used by the Native artifact when dealing with object body params
    */
    suspend fun getPublicQrCodeScanToken(
        overrideHeaders: Map<String, String>? = null,
        methods: Map<String, Any>
    
    ): Result<GetPublicQrCodeScanTokenResponseModel> {
        return getPublicQrCodeScanToken(
            overrideHeaders = overrideHeaders,
            publicQrCodeScanTokenRequest = Json.decodeFromString(methods.getValue("publicQrCodeScanTokenRequest") as String)
        )
    }
}
